/**
 * Sidebar Navigation Configuration
 *
 * Centralized configuration for sidebar navigation items with role-based access control.
 * Separates general admin items from auction house context items.
 */

import React, { type FC } from 'react';
import { AccessLevel, type AccessLevelValue } from './access';
import { build, ROUTES } from './routes';
import {
  BarChartSquare02,
  BookOpen01,
  CalendarMinus01,
  Folder,
  Mail01,
  MessageChatCircle,
  PieChart03,
  Rows01,
  Settings01,
  Tool02,
  UserPlus01,
  Users01,
} from '@untitledui/icons';

export type SidebarItemStatus = 'active' | 'disabled' | 'hidden';

export interface SidebarItem {
  id: string;
  label: string;
  icon: FC<{ className?: string }>;
  href?: string | ((slug: string) => string);
  badge?: React.ReactNode;

  // Role-based status configuration
  // If not specified, defaults to 'active' for all roles
  status?: Partial<Record<AccessLevelValue, SidebarItemStatus>>;

  // Nested menu items
  children?: Array<{
    href: string;
    label: string;
    badge?: number;
  }>;
}

export interface SidebarDivider {
  divider: true;
}

export type SidebarConfig = Array<SidebarItem | SidebarDivider>;

/**
 * Auction house context items - always shown in sidebar
 * Available at /admin and /admin/:slug/* routes
 */
export const AUCTION_HOUSE_ITEMS: SidebarConfig = [
  {
    id: 'navigation.insights',
    label: 'navigation.insights',
    icon: BarChartSquare02,
    href: (slug: string) => build.adminInsights(slug),
  },
  {
    id: 'navigation.sales',
    label: 'navigation.sales',
    icon: CalendarMinus01,
  },
  {
    id: 'navigation.catalogs',
    label: 'navigation.catalogs',
    icon: BookOpen01,
    href: (slug: string) => build.adminCatalogs(slug),
    status: {
      [AccessLevel.AUCTION_HOUSE_ADMIN]: 'disabled',
    }
  },
  {
    id: 'navigation.items',
    label: 'navigation.items',
    icon: Rows01,
    href: (slug: string) => build.adminItems(slug),
    status: {
      [AccessLevel.AUCTION_HOUSE_ADMIN]: 'disabled',
    }
  },
  {
      id: 'navigation.bidders',
      label: 'navigation.bidders',
    icon: Users01,
      href: (slug: string) => build.adminBidders(slug),
    status: {
      [AccessLevel.AUCTION_HOUSE_ADMIN]: 'disabled',
    }
  },
  {
    id: 'navigation.email',
    label: 'navigation.email',
    icon: Mail01,
    status: {
      [AccessLevel.AUCTION_HOUSE_ADMIN]: 'hidden',
      [AccessLevel.AUCTION_HOUSE_WORKER]: 'hidden',
      [AccessLevel.AUCTION_HOUSE_STAFF]: 'hidden',
    },
    children: [
      {
        href: ROUTES.ADMIN_EMAIL_TEMPLATES,
        label: 'navigation.emailTemplates',
      },
      {
        href: ROUTES.ADMIN_EMAIL_LOGS,
        label: 'navigation.emailLogs',
      },
    ],
  },
  {
    id: 'navigation.contracts',
    label: 'navigation.contracts',
    icon: UserPlus01,
  },
  { divider: true },
  {
    id: 'navigation.dailyManagement',
    label: 'navigation.dailyManagement',
    icon: Folder,
    children: [
      {
        href: '',
        label: 'navigation.calendar',
        badge: 18,
      },
      {
        href: '',
        label: 'navigation.tasks',
        badge: 8,
      },
      {
        href: '',
        label: 'navigation.notifications',
        badge: 6,
      },
    ],
  },
  { divider: true },
  {
    id: 'navigation.finance',
    label: 'navigation.finance',
    icon: PieChart03,
    status: {
      [AccessLevel.AUCTION_HOUSE_WORKER]: 'hidden',
    },
  },
  {
    id: 'navigation.settings',
    label: 'navigation.settings',
    icon: Settings01,
    href: ROUTES.ADMIN_SETTINGS,
  },
  {
    id: 'navigation.customerService',
    label: 'navigation.customerService',
    icon: MessageChatCircle,
  },
  {
    id: 'navigation.adminTools',
    label: 'navigation.adminTools',
    icon: Tool02,
    status: {
      [AccessLevel.AUCTION_HOUSE_ADMIN]: 'hidden',
      [AccessLevel.AUCTION_HOUSE_WORKER]: 'hidden',
      [AccessLevel.AUCTION_HOUSE_STAFF]: 'hidden',
    },
    children: [
      {
        href: ROUTES.ADMIN_ASSIGN_ADMINS,
        label: 'navigation.assignAdmins',
      },
      {
        href: ROUTES.ADMIN_SALE_IMPORTS,
        label: 'navigation.saleImports',
      },
      {
        href: ROUTES.ADMIN_AUCTION_HOUSES,
        label: 'navigation.auctionHouses',
      },
      {
        href: ROUTES.ADMIN_ERROR_DEMO,
        label: 'navigation.errorDemo',
      },
    ],
  }
];

/**
 * Get the status of a sidebar item for a specific user access level
 * Defaults to 'active' if no status is specified
 */
export const getItemStatus = (
  item: SidebarItem,
  userAccessLevel: AccessLevelValue
): SidebarItemStatus => {
  return item.status?.[userAccessLevel] ?? 'active';
};

/**
 * Filter sidebar items based on user access level
 * - Removes items with 'hidden' status
 * - Removes orphaned dividers (leading, trailing, consecutive)
 * - Preserves items with 'disabled' status (visible but not clickable)
 */
export const filterSidebarItems = (
  items: SidebarConfig,
  userAccessLevel: AccessLevelValue
): SidebarConfig => {
  const itemsWithStatus = items
    .map(item => {
      if ('divider' in item) return item;
      const status = getItemStatus(item, userAccessLevel);
      return { ...item, computedStatus: status };
    })
    .filter(item => {
      // Keep dividers for now, will filter orphaned ones next
      if ('divider' in item) return true;
      // Remove hidden items
      return (item as SidebarItem & { computedStatus: SidebarItemStatus }).computedStatus !== 'hidden';
    });

  // Remove orphaned dividers (leading, trailing, consecutive)
  return itemsWithStatus.filter((item, i, arr) => {
    if (!('divider' in item)) return true;
    const prevVisible = arr[i - 1] && !('divider' in arr[i - 1]);
    const nextVisible = arr[i + 1] && !('divider' in arr[i + 1]);
    return prevVisible && nextVisible;
  });
};
